import { useCallback, useState } from 'react'

/**
 * MOCKUP-METHOD's validation rule, as one hook: **validate on blur, then live
 * once wrong.**
 *
 * The rule is a compromise between two failure modes, and it is worth stating
 * because both are tempting. Validating on every keystroke from the first one
 * puts *"That does not look like an email address"* under a field holding `p`,
 * which is true and useless — the person is still typing. Validating only on
 * submit means a form with a typo in it looks fine right up until it is
 * refused, and then reports the problem somewhere other than at the field.
 *
 * So: say nothing until the field has been left, and after that keep the
 * verdict current as it is corrected — the one moment feedback is wanted is
 * while somebody is fixing the thing they were just told about.
 *
 * `error` is recomputed by the caller on every render from the current value;
 * this hook owns only the question of *whether to show it yet*. Keeping the
 * rule itself in the screen is deliberate: "is this a mobile number" and "do
 * these two passwords match" have nothing in common but their timing.
 *
 * Used by S-D2 screens 01/02 (the signup email, AUTH-1) and 09 (the reset
 * confirmation, AUTH-16).
 */
export function useBlurValidation(error: string | undefined): {
  /** The message to hand a field's `error` prop, or `undefined` while silent. */
  shown: string | undefined
  /**
   * The verdict itself, whether or not it is being shown yet.
   *
   * A submit handler needs this: pressing Enter from another control is a
   * departure from this field too, and a form that posted a value it had
   * already judged invalid — because the caret never visited the box — would
   * wait for a round trip to be told what it knew.
   */
  error: string | undefined
  /** Attach to the control's `onBlur`. */
  onBlur: () => void
  /**
   * Go quiet again.
   *
   * For the case where the field's *meaning* changed rather than its value —
   * the signup toggle swapping the email field for the mobile one, where
   * carrying "left, and it was wrong" across to a different question would
   * greet the new field with a verdict on the old one.
   */
  reset: () => void
} {
  const [left, setLeft] = useState(false)
  const onBlur = useCallback(() => {
    setLeft(true)
  }, [])
  const reset = useCallback(() => {
    setLeft(false)
  }, [])
  return { shown: left ? error : undefined, error, onBlur, reset }
}

/**
 * Whether a string is shaped like an email address, for the client-side half of
 * `data-validate="required|email"` (`design/screens/auth.html:87`).
 *
 * Deliberately permissive — one `@`, something either side, a dot in the
 * domain. The authority on whether an address is deliverable is the mail that
 * either arrives or does not, and every stricter regex in circulation rejects
 * addresses that are legal under RFC 5322. This exists to catch `priya@gmail`
 * and `priya.gmail.com` before a round trip, not to adjudicate.
 */
export function looksLikeEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
}
